// apps/web/src/pages/pcp/pendencia-sem-roteiro.tsx — Fase 17.3, Plano 05 (D-25).
// Destaque, na Central de Pendências, das pendências de cadastro ou roteiro que faltam:
//  - SEM_ROTEIRO de um lote: "Cadastrar roteiro" leva à lista de peças já filtrada pelo lote (/pcp/roteiros?lote=<id>);
//  - SEM_ROTEIRO por PRODUTO_SEM_CADASTRO: "Cadastrar peça" leva ao cadastro pelo item do PV
//    (/pcp/roteiros/nova?pedidoVendaId=&itemId=) ou, sem os ids do PV, pelo código do cliente (?clienteId=&codigo=);
//  - ITEM_SEM_CONCILIACAO por REMESSA_MANUAL_ITEM_SEM_CADASTRO (remessa manual recusada): "Cadastrar peça" pelo código
//    do cliente, com a pendência (?pendenciaId=) para a tela de cadastro resolvê-la.
// O servidor decide quem pode o quê; aqui o link só aparece para quem tem permissão (consultar a lista; gerenciar para cadastrar).
import type { ReactElement } from 'react';
import type { PendenciaOperacionalRow } from '@pieltec/contracts';

export const TEXTO_LOTE_SEM_ROTEIRO = 'Lote sem roteiro: cadastre o roteiro da peça para gerar a OP.';
export const TEXTO_PRODUTO_SEM_CADASTRO = 'Produto sem cadastro: cadastre a peça e o roteiro para gerar a OP.';
export const TEXTO_REMESSA_SEM_CADASTRO = 'Remessa recusada: o item não tem cadastro. Cadastre a peça e registre a remessa de novo.';
export const ROTULO_CADASTRAR_ROTEIRO = 'Cadastrar roteiro';
export const ROTULO_CADASTRAR_PECA = 'Cadastrar peça';
export const PERMISSAO_VER_ROTEIROS = 'producao:consultar';
export const PERMISSAO_CADASTRAR_PECA = 'roteiros:gerenciar';
export const PERMISSAO_RESOLVER_PENDENCIA = 'remessas:gerenciar';

type PendenciaDeRoteiro = Pick<PendenciaOperacionalRow, 'id' | 'tipo' | 'estado' | 'entidadeTipo' | 'entidadeId' | 'detalheJson'>;

function campo(pendencia: Pick<PendenciaOperacionalRow, 'detalheJson'>, nome: string): string | null {
  const valor = pendencia.detalheJson?.[nome];
  return typeof valor === 'string' && valor.trim() !== '' ? valor : null;
}

function motivo(pendencia: Pick<PendenciaOperacionalRow, 'detalheJson'>): string | null {
  return campo(pendencia, 'motivo');
}

const aberta = (pendencia: Pick<PendenciaOperacionalRow, 'estado'>): boolean => pendencia.estado === 'ABERTA' || pendencia.estado === 'EM_ANALISE';

/** SEM_ROTEIRO de um lote (qualquer motivo): o PCP precisa de roteiro para gerar a OP. */
export function ehSemRoteiro(pendencia: Pick<PendenciaOperacionalRow, 'tipo' | 'entidadeTipo'>): boolean {
  return pendencia.tipo === 'SEM_ROTEIRO' && pendencia.entidadeTipo === 'LOTE';
}

/** SEM_ROTEIRO cujo item do PV não tem Material (produto sem cadastro). */
export function ehProdutoSemCadastro(pendencia: Pick<PendenciaOperacionalRow, 'tipo' | 'entidadeTipo' | 'detalheJson'>): boolean {
  return ehSemRoteiro(pendencia) && motivo(pendencia) === 'PRODUTO_SEM_CADASTRO';
}

/** Remessa manual recusada porque o código do cliente não tem peça cadastrada. */
export function ehRemessaSemCadastro(pendencia: Pick<PendenciaOperacionalRow, 'tipo' | 'detalheJson'>): boolean {
  return pendencia.tipo === 'ITEM_SEM_CONCILIACAO' && motivo(pendencia) === 'REMESSA_MANUAL_ITEM_SEM_CADASTRO';
}

function pelaCodigoDoCliente(pendencia: PendenciaDeRoteiro, comPendencia: boolean): string | null {
  const clienteId = campo(pendencia, 'clienteId');
  const codigo = campo(pendencia, 'codigoItemCliente');
  if (clienteId === null || codigo === null) return null;
  const base = `/pcp/roteiros/nova?clienteId=${encodeURIComponent(clienteId)}&codigo=${encodeURIComponent(codigo)}`;
  return comPendencia ? `${base}&pendenciaId=${encodeURIComponent(pendencia.id)}` : base;
}

/**
 * Atalho da pendência; `null` quando já resolvida, quando faltam os campos necessários ou quando o tipo não é de roteiro/cadastro.
 * A ordem importa: produto sem cadastro é um SEM_ROTEIRO, mas o atalho é o cadastro da peça, não a lista.
 */
export function linkDaPendencia(pendencia: PendenciaDeRoteiro): string | null {
  if (!aberta(pendencia)) return null;
  if (ehRemessaSemCadastro(pendencia)) return pelaCodigoDoCliente(pendencia, true);
  if (ehProdutoSemCadastro(pendencia)) {
    const pedidoVendaId = campo(pendencia, 'pedidoVendaId');
    const itemId = campo(pendencia, 'pedidoVendaItemId');
    if (pedidoVendaId !== null && itemId !== null) {
      return `/pcp/roteiros/nova?pedidoVendaId=${encodeURIComponent(pedidoVendaId)}&itemId=${encodeURIComponent(itemId)}`;
    }
    return pelaCodigoDoCliente(pendencia, false);
  }
  if (ehSemRoteiro(pendencia)) return `/pcp/roteiros?lote=${encodeURIComponent(pendencia.entidadeId)}`;
  return null;
}

/** Texto da pendência; `null` para tipos que não são de roteiro ou cadastro. */
export function textoDaPendencia(pendencia: PendenciaDeRoteiro): string | null {
  if (ehRemessaSemCadastro(pendencia)) return TEXTO_REMESSA_SEM_CADASTRO;
  if (ehProdutoSemCadastro(pendencia)) return TEXTO_PRODUTO_SEM_CADASTRO;
  if (ehSemRoteiro(pendencia)) return TEXTO_LOTE_SEM_ROTEIRO;
  return null;
}

function cadastraPeca(pendencia: PendenciaDeRoteiro): boolean {
  return ehRemessaSemCadastro(pendencia) || ehProdutoSemCadastro(pendencia);
}

interface DestaqueSemRoteiroProps {
  readonly pendencia: PendenciaDeRoteiro;
  /** Permissões da sessão; `null` enquanto carrega (fail-closed: só o texto aparece). */
  readonly permissoes: readonly string[] | null;
}

export function DestaqueSemRoteiro({ pendencia, permissoes }: DestaqueSemRoteiroProps): ReactElement | null {
  const texto = textoDaPendencia(pendencia);
  if (texto === null) return null;
  const link = linkDaPendencia(pendencia);
  const pecas = cadastraPeca(pendencia);
  // O link da remessa recusada leva `?pendenciaId=`: cadastrar a peça resolve a pendência, e a API exige remessas:gerenciar (P2-CR-01).
  const resolvePendencia = ehRemessaSemCadastro(pendencia);
  const permitido =
    permissoes !== null
    && permissoes.includes(PERMISSAO_VER_ROTEIROS)
    && (!pecas || permissoes.includes(PERMISSAO_CADASTRAR_PECA))
    && (!resolvePendencia || permissoes.includes(PERMISSAO_RESOLVER_PENDENCIA));
  return (
    <div data-testid="pendencia-sem-roteiro" className="mt-1 space-y-0.5 font-normal">
      <p className="text-rose-800 font-semibold">{texto}</p>
      {link !== null && permitido && (
        <a href={link} className="text-blue-700 underline font-medium">
          {pecas ? ROTULO_CADASTRAR_PECA : ROTULO_CADASTRAR_ROTEIRO}
        </a>
      )}
    </div>
  );
}
